/**
 * Wallet balance events. Two types rather than one with a severity: one warns, the
 * other reports that AI usage has already stopped. Mutually exclusive — the emitter
 * classifies the balance into exactly one zone.

 */

import type { TenantEventDefinition } from './definition'

export const BILLING_LOW_BALANCE_EVENT_TYPE = 'billing_low_balance' as const
export const BILLING_BALANCE_DEPLETED_EVENT_TYPE = 'billing_balance_depleted' as const

export interface BillingLowBalancePayload {
  /** Wallet balance after the write that triggered the alert, in microUSD. */
  balanceMicrousd: number
  /** The threshold that was crossed, in microUSD. */
  thresholdMicrousd: number
}

export interface BillingBalanceDepletedPayload {
  /** Wallet balance after the write, in microUSD. Zero or negative by definition. */
  balanceMicrousd: number
}

function isFiniteInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isLowBalancePayload(payload: unknown): payload is BillingLowBalancePayload {
  if (typeof payload !== 'object' || payload === null) return false
  const p = payload as Record<string, unknown>
  return isFiniteInteger(p.balanceMicrousd) && isFiniteInteger(p.thresholdMicrousd)
}

function isBalanceDepletedPayload(payload: unknown): payload is BillingBalanceDepletedPayload {
  if (typeof payload !== 'object' || payload === null) return false
  return isFiniteInteger((payload as Record<string, unknown>).balanceMicrousd)
}

export const billingLowBalanceEventDefinition: TenantEventDefinition<BillingLowBalancePayload> =
  {
    eventType: BILLING_LOW_BALANCE_EVENT_TYPE,
    supportedChannelTypes: ['email', 'telegram'],
    validatePayload: isLowBalancePayload,
    tenant: {
      label: 'Low balance',
      description: "The balance fell to the tenant's low-balance threshold.",
      recipients: 'account_alerts',
      mandatory: false,
    },
  }

export const billingBalanceDepletedEventDefinition: TenantEventDefinition<BillingBalanceDepletedPayload> =
  {
    eventType: BILLING_BALANCE_DEPLETED_EVENT_TYPE,
    supportedChannelTypes: ['email', 'telegram'],
    validatePayload: isBalanceDepletedPayload,
    tenant: {
      label: 'Balance depleted',
      description: 'The balance reached zero, so AI features stopped.',
      recipients: 'account_alerts',
      mandatory: false,
    },
  }
